import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, AlertTriangle, CheckCircle, Sparkles, Loader2, FileDown, Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { adminApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { AuditLogButton } from '@/components/AuditLogPanel';
import { statusLabels, statusColors, getDaysRemaining } from './reportDetail/helpers';
import { AISuggestionsBanner } from './reportDetail/AISuggestionsBanner';
import { AIAnalysisCard } from './reportDetail/AIAnalysisCard';
import { DetailsCard } from './reportDetail/DetailsCard';
import { AttachmentsCard } from './reportDetail/AttachmentsCard';
import { MessagesCard } from './reportDetail/MessagesCard';
import { ComplianceClockCard } from './reportDetail/ComplianceClockCard';
import { CaseManagementCard } from './reportDetail/CaseManagementCard';
import { ReporterInfoCard } from './reportDetail/ReporterInfoCard';
import CaseTimelineCard from './reportDetail/CaseTimelineCard';
import WorkflowApprovalCard from './reportDetail/WorkflowApprovalCard';
import StaleAwaitingInfoAlert from './reportDetail/StaleAwaitingInfoAlert';
import CloseReportDialog from './reportDetail/CloseReportDialog';
import { ImageLightbox } from './reportDetail/ImageLightbox';

// Roles that can assign reports to other users.
const ASSIGNER_ROLES = new Set(['company_admin', 'compliance_officer', 'triage_manager', 'super_admin']);

const ReportDetailPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [analyzingAI, setAnalyzingAI] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [approvingAI, setApprovingAI] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [archiveSuggestion, setArchiveSuggestion] = useState({ open: false, message: '' });
  const timelineRefresh = useRef(null);

  const canAssign = ASSIGNER_ROLES.has(user?.role);

  const fetchReport = useCallback(async () => {
    try {
      const response = await adminApi.getReport(reportId);
      setReport(response.data);
      if (response.data.ai_analysis) {
        setAiAnalysis(response.data.ai_analysis);
      }
      if (response.data.ai_suggestions) {
        setAiSuggestions(response.data.ai_suggestions);
      }
    } catch (err) {
      setError('Erro ao carregar denúncia');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Load the tenant users list once so the "Atribuir a" dropdown in
  // CaseManagementCard has candidates. Only assigner roles need this.
  useEffect(() => {
    if (!canAssign) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await adminApi.getUsers();
        if (!cancelled) setTenantUsers(res.data || []);
      } catch (_) {
        // Non-fatal — the card just won't offer assignees.
      }
    })();
    return () => { cancelled = true; };
  }, [canAssign]);

  const handleAIAnalysis = async () => {
    setAnalyzingAI(true);
    setError('');
    try {
      const response = await adminApi.analyzeReport(reportId);
      setAiAnalysis(response.data);
      if (response.data.ai_enabled && !response.data.error) {
        setAiSuggestions({
          suggested_category: response.data.suggested_category,
          suggested_severity: response.data.severity,
          status: 'PENDENTE_APROVACAO',
          analyzed_at: response.data.analyzed_at
        });
      }
      setSuccessMsg('Análise IA concluída');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro na análise IA');
    } finally {
      setAnalyzingAI(false);
    }
  };

  const handleApproveAISuggestions = async (action, customCategory = null, customSeverity = null) => {
    setApprovingAI(true);
    setError('');
    try {
      const data = { action };
      if (customCategory) data.custom_category = customCategory;
      if (customSeverity) data.custom_severity = customSeverity;

      await adminApi.approveAISuggestions(reportId, data);
      await fetchReport();
      setSuccessMsg(action === 'APROVAR' ? 'Sugestões IA aprovadas e aplicadas' : 'Sugestões IA rejeitadas');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao processar sugestões');
    } finally {
      setApprovingAI(false);
    }
  };

  const handleUpdate = async (field, value, extras = {}) => {
    // Special sentinel: sub-dialogs (Close, Reopen, Approval) already performed
    // the API call and only need the parent to refetch + refresh the timeline.
    if (field === '__refetch__') {
      await fetchReport();
      if (timelineRefresh.current) timelineRefresh.current();
      return;
    }
    setUpdating(true);
    setError('');
    try {
      await adminApi.updateReport(reportId, { [field]: value, ...extras });
      await fetchReport();
      if (timelineRefresh.current) timelineRefresh.current();
      setSuccessMsg('Atualizado com sucesso');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendAcknowledgment = async () => {
    setUpdating(true);
    try {
      await adminApi.sendAcknowledgment(reportId);
      await fetchReport();
      setSuccessMsg('Confirmação de receção registada');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError('Erro ao enviar confirmação');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      await adminApi.sendMessage(reportId, newMessage);
      await fetchReport();
      setNewMessage('');
    } catch (err) {
      setError('Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    setError('');

    try {
      await adminApi.uploadAttachment(reportId, file);
      await fetchReport();
      setSuccessMsg('Ficheiro enviado com sucesso');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar ficheiro');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadAttachment = async (attachmentId, filename) => {
    try {
      const response = await adminApi.downloadAttachment(attachmentId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Erro ao descarregar ficheiro');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Tem a certeza que deseja eliminar este ficheiro?')) return;

    try {
      await adminApi.deleteAttachment(attachmentId);
      await fetchReport();
      setSuccessMsg('Ficheiro eliminado');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError('Erro ao eliminar ficheiro');
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    setError('');

    try {
      const response = await adminApi.exportPdf(reportId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Relatorio_${report.tracking_code}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccessMsg('PDF exportado com sucesso');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError('Erro ao exportar PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">A carregar...</div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Denúncia não encontrada</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/denuncias')}>
          Voltar à lista
        </Button>
      </div>
    );
  }

  const ackDays = getDaysRemaining(report.acknowledgment_deadline);
  const feedbackDays = getDaysRemaining(report.feedback_deadline);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/denuncias')}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-mono" data-testid="report-tracking-code">
              {report.tracking_code}
            </h1>
            <p className="text-slate-500">{report.category}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleAIAnalysis}
            disabled={analyzingAI}
            data-testid="ai-analyze-btn"
          >
            {analyzingAI ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                A analisar...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Analisar com IA
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={handleExportPdf}
            disabled={exportingPdf}
            data-testid="export-pdf-btn"
          >
            <FileDown className="w-4 h-4 mr-2" />
            {exportingPdf ? 'A exportar...' : 'Exportar PDF'}
          </Button>
          <AuditLogButton reportId={reportId} />
          <Badge className={statusColors[report.status]} data-testid="report-status">
            {statusLabels[report.status]}
          </Badge>
        </div>
      </div>

      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <AlertDescription className="text-red-700">{error}</AlertDescription>
        </Alert>
      )}

      {successMsg && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <AlertDescription className="text-emerald-700">{successMsg}</AlertDescription>
        </Alert>
      )}

      {/* Management lock warning (plan limit exceeded, add-on confirmation required) */}
      {report.management_locked && (
        <Alert className="bg-red-50 border-red-300" data-testid="management-lock-banner">
          <Lock className="w-4 h-4 text-red-600" />
          <AlertDescription className="text-red-800 flex items-center justify-between gap-4">
            <span>
              <strong>Gestão bloqueada:</strong> esta denúncia excede o limite mensal do plano. A denúncia foi recebida, mas para a tratar é necessário confirmar um add-on de denúncias extra.
            </span>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white flex-shrink-0"
              onClick={() => navigate('/admin/faturacao')} data-testid="goto-billing-btn">
              Resolver na Faturação
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <AISuggestionsBanner
        aiSuggestions={aiSuggestions}
        approvingAI={approvingAI}
        onApprove={handleApproveAISuggestions}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Descrição dos Factos</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 whitespace-pre-wrap">{report.description}</p>
            </CardContent>
          </Card>

          <AIAnalysisCard aiAnalysis={aiAnalysis} />

          <StaleAwaitingInfoAlert
            report={report}
            onSuggestArchive={(msg) => setArchiveSuggestion({ open: true, message: msg })}
          />

          <DetailsCard report={report} />

          <AttachmentsCard
            report={report}
            uploadingFile={uploadingFile}
            fileInputRef={fileInputRef}
            onFileUpload={handleFileUpload}
            onDownload={handleDownloadAttachment}
            onDelete={handleDeleteAttachment}
            onPreviewClick={setLightboxImage}
          />

          <MessagesCard
            report={report}
            newMessage={newMessage}
            setNewMessage={setNewMessage}
            sendingMessage={sendingMessage}
            onSendMessage={handleSendMessage}
          />

          <CaseTimelineCard reportId={reportId} ref={timelineRefresh} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <ComplianceClockCard
            report={report}
            ackDays={ackDays}
            feedbackDays={feedbackDays}
            updating={updating}
            onSendAcknowledgment={handleSendAcknowledgment}
          />

          <CaseManagementCard
            report={report}
            updating={updating}
            onUpdate={handleUpdate}
            users={tenantUsers}
            canAssign={canAssign}
          />

          <WorkflowApprovalCard
            report={report}
            currentUserRole={user?.role}
            onChanged={() => handleUpdate('__refetch__')}
          />

          <ReporterInfoCard report={report} />
        </div>
      </div>

      <ImageLightbox
        image={lightboxImage}
        onClose={() => setLightboxImage(null)}
        onDownload={handleDownloadAttachment}
      />

      <CloseReportDialog
        open={archiveSuggestion.open}
        onOpenChange={(o) => setArchiveSuggestion((s) => ({ ...s, open: o }))}
        report={report}
        defaultStatus="ARQUIVADA"
        initialMessage={archiveSuggestion.message}
        onClosed={() => handleUpdate('__refetch__')}
      />
    </div>
  );
};

export default ReportDetailPage;
